import { validateClaimImageFile } from '@/domains/paydirt/claims/claimImageUpload';
import type { WeightUnit } from '@/domains/paydirt/materialUnits';
import { apiFetch } from '@/platform/lib/api';

export type ClaimSummary = {
  id: string;
  title: string;
  userId?: string;
  roiPct?: number;
  status?: string;
  createdAt?: string;
  communityApproved?: boolean;
  communityValidity?: {
    communityValidityRating?: number;
    validityPositiveMass?: number;
  };
  hasVideo?: boolean;
};

export type ClaimMaterialLine = {
  id?: string;
  materialId: string;
  inputWeightQty?: number;
  inputWeightUnit?: string;
  weightQty?: number;
  weightUnit?: string;
  proofImagePath?: string;
  proofImageUrl?: string;
  lineValueUsd?: number;
  lineEstimatedValueUsd?: number;
  valuationMethod?: string;
  ownerPricePerUnitUsd?: number;
  communityPricePerUnitUsd?: number;
  communityPriceSampleCount?: number;
};

export type DraftClaimFull = ClaimSummary & {
  description?: string;
  purchasePriceUsd?: number;
  estimatedValueUsd?: number;
  acceptedEstimatedValueUsd?: number;
  acceptedRoiPct?: number;
  proofImagePath?: string;
  proofImageUrl?: string;
  scaleZeroImagePath?: string;
  scaleZeroImageUrl?: string;
  youtubeVideoUrl?: string;
  youtubeVideoId?: string;
  hasVideo?: boolean;
  hasPurchasePage?: boolean;
  purchasePageUrl?: string;
  purchasePageLabel?: string;
  vendorLinkStatus?: 'none' | 'pending' | 'approved' | 'rejected';
  sellerUserId?: string;
  location?: {
    coordinates?: { lat?: number; lng?: number };
    placeLabel?: string;
  };
  materials?: ClaimMaterialLine[];
};

export type ClaimsListResponse = {
  claims: ClaimSummary[];
  nextCursor?: string;
};

export function listClaims(params: {
  status?: string;
  userId?: string;
  q?: string;
  limit?: number;
  cursor?: string;
}) {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.userId) q.set('userId', params.userId);
  if (params.q) q.set('q', params.q);
  if (params.limit) q.set('limit', String(params.limit));
  if (params.cursor) q.set('cursor', params.cursor);
  const qs = q.toString();
  return apiFetch<ClaimsListResponse>(`/claims${qs ? `?${qs}` : ''}`);
}

export function getClaim(id: string) {
  return apiFetch<DraftClaimFull>(`/claims/${id}`);
}

export type PatchDraftClaimInput = {
  title?: string;
  description?: string;
  purchasePriceUsd?: number;
  lat?: number;
  lng?: number;
  placeLabel?: string;
  materials?: Array<{
    id?: string;
    materialId: string;
    weightQty: number;
    inputWeightUnit?: string;
  }>;
  youtubeVideoUrl?: string;
};

export function patchClaimDraft(id: string, body: PatchDraftClaimInput) {
  return apiFetch<DraftClaimFull>(`/claims/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function takeDownClaim(id: string) {
  return apiFetch<DraftClaimFull>(`/claims/${id}/take-down`, { method: 'POST' });
}

export function deleteClaim(id: string) {
  return apiFetch<void>(`/claims/${id}`, { method: 'DELETE' });
}

async function uploadClaimImage(
  claimId: string,
  file: File,
  opts?: { materialLineId?: string; scaleZero?: boolean },
) {
  const validationError = validateClaimImageFile(file);
  if (validationError) {
    throw new Error(validationError);
  }
  const signed = await apiFetch<{ uploadUrl: string; objectPath: string; publicUrl?: string }>(
    '/uploads/signed-url',
    {
      method: 'POST',
      body: JSON.stringify({
        claimId,
        filename: file.name,
        contentType: file.type || 'image/jpeg',
        materialLineId: opts?.materialLineId,
        scaleZero: opts?.scaleZero ?? false,
      }),
    },
  );
  if (!signed.uploadUrl.includes('signed=stub')) {
    const put = await fetch(signed.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    });
    if (!put.ok) {
      throw new Error('Upload failed');
    }
  }
  return signed;
}

export async function uploadScaleZeroProof(claimId: string, file: File) {
  const signed = await uploadClaimImage(claimId, file, { scaleZero: true });
  return apiFetch<{ scaleZeroImagePath: string; scaleZeroImageUrl: string }>(
    `/claims/${claimId}/scale-zero-proof`,
    {
      method: 'PATCH',
      body: JSON.stringify({ scaleZeroImagePath: signed.objectPath }),
    },
  );
}

export async function uploadLineProof(claimId: string, lineId: string, file: File) {
  const signed = await uploadClaimImage(claimId, file, { materialLineId: lineId });
  return apiFetch<{ proofImagePath: string; proofImageUrl: string }>(
    `/claims/${claimId}/materials/${lineId}/proof`,
    {
      method: 'PATCH',
      body: JSON.stringify({ proofImagePath: signed.objectPath }),
    },
  );
}

export function materialLinesFromClaim(
  materials: DraftClaimFull['materials'],
): Array<{
  materialId: string;
  weightQty: string;
  inputWeightUnit: WeightUnit;
  id?: string;
  proofImagePath?: string;
  proofImageUrl?: string;
}> {
  if (!materials?.length) {
    return [{ materialId: 'gold', weightQty: '', inputWeightUnit: 'troy_oz' }];
  }
  return materials.map((m) => ({
    id: m.id,
    materialId: m.materialId,
    weightQty: String(m.inputWeightQty ?? m.weightQty ?? ''),
    inputWeightUnit: (m.inputWeightUnit ?? m.weightUnit ?? 'troy_oz') as WeightUnit,
    proofImagePath: m.proofImagePath,
    proofImageUrl: m.proofImageUrl,
  }));
}
